// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the private scratchpad. One pad per user, autosaved with a short
// debounce and kept in step across the user's devices through
// KONVO_SCRATCHPAD_UPDATE gateway events; a tab ignores the echo of its own save
// by client id.
//
// Every save says which server version it was edited from (base_version and
// base_content), so the server merges instead of replacing when another device saved
// in between. Nothing is saved before the pad has loaded once: typing done before
// then is merged into the loaded text. (On 25 Sep 2026 a failed load during an API
// restart left a tab with an empty pad, and opening its board saved that over the
// real notes.)

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {BoardScope} from '@app/features/konvo_board/state/KonvoBoards';
import {
	cleanPadName,
	DEFAULT_PAD,
	MAX_PADS,
	newBoardId,
	type Pad,
	parsePads,
	serialisePads,
} from '@app/features/konvo_scratchpad/state/ScratchpadPads';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {mergeLines} from '@fluxer/constants/src/LineMerge';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('KonvoScratchpad');
const SAVE_DEBOUNCE_MS = 600;
const RETRY_MS = 5000;
const ACTIVE_PAD_KEY = 'konvo:scratchpad:pad';
const EDITED_KEY = 'konvo:scratchpad:edited';
const SHARED_KEY = 'konvo:scratchpad:shared';
const SIDE_KEY = 'konvo:scratchpad:side';
const LAYOUT_KEY = 'konvo:scratchpad:board-layout';
const MAX_REMEMBERED_SHARED = 12;

function readJson<T>(key: string, fallback: T): T {
	try {
		const raw = getProtectedLocalStorage()?.getItem(key);
		return raw ? (JSON.parse(raw) as T) : fallback;
	} catch {
		return fallback;
	}
}

function writeJson(key: string, value: unknown): void {
	try {
		getProtectedLocalStorage()?.setItem(key, JSON.stringify(value));
	} catch {
		// storage unavailable
	}
}

function isPhoneLayout(): boolean {
	return typeof document !== 'undefined' && document.documentElement.classList.contains('mobile-layout');
}

/** What the drawer shows: every pad at a glance, or one pad. */
export type ScratchpadView = 'home' | 'pad';

/** Which side of a pad is showing: the notes, or its whiteboard. */
export type PadSide = 'notes' | 'board';

/** How much room a board takes: 60% of the window, or all of it with the notes beside it. */
export type BoardLayout = 'wide' | 'full';

/** A line to scroll to once its pad is open: `padKey` is 'personal:<name>' or 'shared:<channelId>'. */
export interface ScratchpadJump {
	padKey: string;
	/** The line's words, as ScratchpadOverview.plainLine(markdown, true) gives them. */
	text: string;
}

function readActivePad(): string {
	try {
		return getProtectedLocalStorage()?.getItem(ACTIVE_PAD_KEY) ?? DEFAULT_PAD;
	} catch {
		return DEFAULT_PAD;
	}
}
export const SCRATCHPAD_MAX_CHARS = 100_000;

export interface ScratchpadPayload {
	content: string;
	version: number;
	updated_at: string | null;
	client_id?: string | null;
	/** Set on a save the server merged with another device's save. */
	merged?: boolean;
}

/** An earlier version of the pad, as GET /konvo/scratchpad/history lists it. */
export interface ScratchpadRevisionSummary {
	version: number;
	saved_at: string;
	chars: number;
	preview: Array<string>;
}

export type ScratchpadStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'offline';

function randomClientId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	}
}

class KonvoScratchpadStore {
	open = false;
	content = '';
	/** The server version `baseContent` is. */
	version = 0;
	updatedAt: string | null = null;
	status: ScratchpadStatus = 'idle';
	/** True once the server's pad has been read (or pushed) here; nothing is saved before. */
	loaded = false;
	/** True while local edits have not reached the server yet. */
	dirty = false;
	/** Name of the pad being edited; the others stay in `content` untouched. */
	activePad = readActivePad();
	/** When set, the drawer shows this chat's shared pad instead of the personal pads. */
	sharedChannelId: string | null = null;
	view: ScratchpadView = 'pad';
	/** True when the pad was opened from All pads, so going back returns there. */
	fromHome = false;
	/** All pads opens with its search field focused. */
	searchOnHome = false;
	jump: ScratchpadJump | null = null;
	/** When each personal pad was last changed on this device (ms), keyed by pad name. */
	editedAt: Record<string, number> = readJson(EDITED_KEY, {});
	/** Chats whose shared pad was opened here, most recent first; All pads lists them. */
	sharedIds: Array<string> = readJson<Array<string>>(SHARED_KEY, []).filter((id) => typeof id === 'string');
	/** The side each pad was left on, on this device, keyed like `padKey`. */
	padSides: Record<string, PadSide> = readJson<Record<string, PadSide>>(SIDE_KEY, {});
	boardLayout: BoardLayout = readJson<BoardLayout>(LAYOUT_KEY, 'wide') === 'full' ? 'full' : 'wide';
	readonly clientId = randomClientId();
	/** The text as the server last had it, which unsaved edits are based on. */
	private baseContent = '';
	private saveTimer: ReturnType<typeof setTimeout> | null = null;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	private inFlight: Promise<void> | null = null;
	private listenersBound = false;

	constructor() {
		makeAutoObservable<this, 'saveTimer' | 'retryTimer' | 'inFlight' | 'listenersBound' | 'baseContent'>(
			this,
			{
				saveTimer: false,
				retryTimer: false,
				inFlight: false,
				listenersBound: false,
				clientId: false,
				baseContent: false,
			},
			{autoBind: true},
		);
	}

	/**
	 * The rail button, More and Mod+Shift+E. On the phone the scratchpad opens on All pads;
	 * on desktop it comes back to whatever was open last.
	 */
	toggle(): void {
		if (this.open) {
			this.setOpen(false);
			return;
		}
		if (isPhoneLayout()) this.openHome();
		else this.setOpen(true);
	}

	/** Opens the drawer straight on a chat's shared pad (chat header, "shared pad updated" messages). */
	openShared(channelId: string): void {
		this.sharedChannelId = channelId;
		this.view = 'pad';
		this.fromHome = false;
		this.rememberShared(channelId);
		this.setOpen(true);
	}

	showPersonal(): void {
		this.sharedChannelId = null;
		this.view = 'pad';
	}

	/** Switches the open drawer to a chat's shared pad (the pad chips), keeping where "back" goes. */
	showShared(channelId: string): void {
		this.sharedChannelId = channelId;
		this.view = 'pad';
		this.rememberShared(channelId);
	}

	/** Switches to one of the personal pads (the pad chips). */
	showPad(name: string): void {
		this.flush();
		this.selectPad(name);
		this.sharedChannelId = null;
		this.view = 'pad';
	}

	/** All pads: the personal pads and the shared chat pads as cards. */
	openHome(options: {search?: boolean} = {}): void {
		this.flush();
		this.sharedChannelId = null;
		this.view = 'home';
		this.searchOnHome = Boolean(options.search);
		this.setOpen(true);
	}

	/** Opens one personal pad, optionally scrolling to a line in it. */
	openPad(name: string, jumpText?: string): void {
		if (this.view === 'home') this.fromHome = true;
		this.flush();
		this.selectPad(name);
		this.sharedChannelId = null;
		this.view = 'pad';
		this.jump = jumpText ? {padKey: `personal:${name}`, text: jumpText} : null;
		this.setOpen(true);
	}

	/** Opens a shared pad from All pads, optionally scrolling to a line in it. */
	openSharedFromHome(channelId: string, jumpText?: string): void {
		this.openShared(channelId);
		this.fromHome = true;
		this.jump = jumpText ? {padKey: `shared:${channelId}`, text: jumpText} : null;
	}

	/** The top-left control of a pad: back to All pads when it came from there, else close. */
	back(): void {
		if (this.view === 'pad' && this.fromHome) {
			this.openHome();
			return;
		}
		this.setOpen(false);
	}

	clearJump(): void {
		this.jump = null;
	}

	rememberShared(channelId: string): void {
		const next = [channelId, ...this.sharedIds.filter((id) => id !== channelId)].slice(0, MAX_REMEMBERED_SHARED);
		if (next.join() === this.sharedIds.join()) return;
		this.sharedIds = next;
		writeJson(SHARED_KEY, next);
	}

	/**
	 * When a pad was last edited: this device's record. The server only knows when the whole
	 * document was saved, so that time is only claimed for the pad that was open last.
	 */
	padEditedAt(name: string): Date | null {
		const at = this.editedAt[name];
		if (at) return new Date(at);
		return name === this.pad.name && this.updatedAt ? new Date(this.updatedAt) : null;
	}

	private markEdited(name: string): void {
		this.editedAt = {...this.editedAt, [name]: Date.now()};
		writeJson(EDITED_KEY, this.editedAt);
	}

	get pads(): Array<Pad> {
		return parsePads(this.content);
	}

	/** 'personal:<name>' or 'shared:<channelId>' for the pad that is open. */
	get padKey(): string {
		return this.sharedChannelId ? `shared:${this.sharedChannelId}` : `personal:${this.pad.name}`;
	}

	/** The side of the open pad: notes unless it was left on its board. */
	get side(): PadSide {
		return this.padSides[this.padKey] === 'board' ? 'board' : 'notes';
	}

	/** The open pad's board, if it has one (a chat's pad always can). */
	get boardScope(): BoardScope | null {
		if (this.sharedChannelId) return {kind: 'channel', channelId: this.sharedChannelId};
		const board = this.pad.board;
		return board ? {kind: 'personal', boardId: board} : null;
	}

	/** The board of any personal pad, for thumbnails. */
	boardScopeFor(name: string): BoardScope | null {
		const board = this.pads.find((pad) => pad.name === name)?.board;
		return board ? {kind: 'personal', boardId: board} : null;
	}

	/** Notes | Board. Turning a personal pad to its board for the first time gives it one. */
	setSide(side: PadSide): void {
		if (side === 'board' && !this.sharedChannelId) this.ensureBoard(this.pad.name);
		const key = this.padKey;
		if ((this.padSides[key] ?? 'notes') === side) return;
		this.padSides = {...this.padSides, [key]: side};
		writeJson(SIDE_KEY, this.padSides);
	}

	setBoardLayout(layout: BoardLayout): void {
		this.boardLayout = layout;
		writeJson(LAYOUT_KEY, layout);
	}

	/** Gives a personal pad a board id (kept in its marker line) and returns it. */
	ensureBoard(name: string): string | null {
		// The pads are not known until the pad has loaded; a board made now would be saved
		// onto an empty document.
		if (!this.loaded) {
			void this.load();
			return null;
		}
		const pads = this.pads;
		const current = pads.find((pad) => pad.name === name);
		if (!current) return null;
		if (current.board) return current.board;
		const board = newBoardId();
		this.setContent(serialisePads(pads.map((pad) => (pad.name === name ? {...pad, board} : pad))));
		return board;
	}

	/** The active pad, falling back to the first one if it was renamed or deleted elsewhere. */
	get pad(): Pad {
		const pads = this.pads;
		return pads.find((pad) => pad.name === this.activePad) ?? pads[0];
	}

	get padContent(): string {
		return this.pad.content;
	}

	selectPad(name: string): void {
		this.activePad = name;
		try {
			getProtectedLocalStorage()?.setItem(ACTIVE_PAD_KEY, name);
		} catch {
			// storage unavailable
		}
	}

	/** Replaces the active pad's text and saves the whole document. */
	setPadContent(next: string): void {
		this.updatePad(this.pad.name, next);
	}

	/** Replaces any personal pad's text, e.g. ticking a line from All pads. */
	updatePad(name: string, next: string): void {
		const pads = this.pads;
		const current = pads.find((pad) => pad.name === name);
		if (!current || current.content === next) return;
		this.setContent(serialisePads(pads.map((pad) => (pad.name === name ? {...pad, content: next} : pad))));
		this.markEdited(name);
	}

	addPad(rawName: string): boolean {
		if (!this.loaded) return false;
		const pads = this.pads;
		const name = cleanPadName(rawName, pads);
		if (!name || pads.length >= MAX_PADS) return false;
		this.setContent(serialisePads([...pads, {name, content: ''}]));
		this.markEdited(name);
		this.selectPad(name);
		return true;
	}

	renamePad(from: string, rawName: string): boolean {
		if (!this.loaded) return false;
		const pads = this.pads;
		const name = cleanPadName(rawName, pads, from);
		if (!name) return false;
		this.setContent(serialisePads(pads.map((pad) => (pad.name === from ? {...pad, name} : pad))));
		if (this.activePad === from) this.selectPad(name);
		return true;
	}

	deletePad(name: string): void {
		if (!this.loaded) return;
		const pads = this.pads.filter((pad) => pad.name !== name);
		this.setContent(serialisePads(pads.length > 0 ? pads : [{name: DEFAULT_PAD, content: ''}]));
		if (this.activePad === name) this.selectPad(pads[0]?.name ?? DEFAULT_PAD);
	}

	setOpen(open: boolean): void {
		this.open = open;
		if (!open) {
			this.fromHome = false;
			this.searchOnHome = false;
			this.jump = null;
		}
		if (open) {
			this.bindListeners();
			if (!this.loaded) void this.load();
		}
	}

	async load(): Promise<void> {
		if (this.dirty && this.loaded) return; // never clobber unsaved typing
		runInAction(() => {
			if (!this.loaded) this.status = 'loading';
		});
		try {
			const response = await http.get<ScratchpadPayload>(Endpoints.KONVO_SCRATCHPAD, {mode: 'silent'});
			if (!response.ok) throw new Error(`load failed with ${response.status}`);
			const body = response.body;
			if (!body || typeof body.content !== 'string') throw new Error('load returned no pad');
			let resave = false;
			runInAction(() => {
				const server = body.content ?? '';
				if (this.dirty) {
					if (this.loaded) return;
					// Typed before the pad had loaded: keep those lines on top of the real pad.
					const merged = mergeLines(this.baseContent, this.content, server);
					this.content = merged.conflict ? `${this.content.replace(/\n+$/, '')}\n${server}` : merged.text;
					resave = this.content !== server;
					this.dirty = resave;
				} else {
					this.content = server;
				}
				this.baseContent = server;
				this.version = body.version ?? 0;
				this.updatedAt = body.updated_at ?? null;
				this.loaded = true;
				this.status = resave ? 'saving' : 'saved';
			});
			if (resave) this.scheduleSave();
		} catch (error) {
			logger.warn('Failed to load scratchpad', error);
			runInAction(() => {
				this.status = this.loaded ? this.status : 'offline';
			});
		}
	}

	/** Called on every keystroke; the save itself is debounced. */
	setContent(next: string): void {
		const value = next.length > SCRATCHPAD_MAX_CHARS ? next.slice(0, SCRATCHPAD_MAX_CHARS) : next;
		if (value === this.content) return;
		this.content = value;
		this.dirty = true;
		this.status = 'saving';
		this.scheduleSave();
	}

	/** Adds lines to the end of the active pad (used by "Add to Scratchpad" in chats). */
	appendToPad(markdown: string): void {
		const current = this.padContent.replace(/\s+$/, '');
		this.setPadContent(current ? `${current}\n${markdown}` : markdown);
		// Whatever opens the drawer next should show where the line went.
		this.sharedChannelId = null;
		this.view = 'pad';
	}

	/** Empties the active pad only. */
	clear(): void {
		this.setPadContent('');
	}

	/** Flushes the pending save immediately (drawer close, tab hide). */
	flush(): void {
		if (this.saveTimer != null) {
			clearTimeout(this.saveTimer);
			this.saveTimer = null;
		}
		if (this.dirty) void this.save();
	}

	/** Gateway: another session (or this one, echoed) saved the pad. */
	applyRemote(payload: ScratchpadPayload): void {
		if (payload.client_id && payload.client_id === this.clientId) {
			// Our own save coming back around; the save's response moves the base along.
			this.updatedAt = payload.updated_at ?? this.updatedAt;
			return;
		}
		if (payload.version <= this.version && this.loaded) return;
		// Unsaved local typing is merged into this by the server when it is saved, against
		// the base it was typed on, so leave both alone.
		if (this.dirty) return;
		this.content = payload.content ?? '';
		this.baseContent = this.content;
		this.version = payload.version;
		this.updatedAt = payload.updated_at ?? null;
		this.loaded = true;
		this.status = 'saved';
	}

	private scheduleSave(): void {
		if (this.saveTimer != null) clearTimeout(this.saveTimer);
		this.saveTimer = setTimeout(() => {
			this.saveTimer = null;
			void this.save();
		}, SAVE_DEBOUNCE_MS);
	}

	private async save(): Promise<void> {
		if (this.inFlight) {
			// A save is running; make sure another follows with the latest text.
			this.scheduleSave();
			return;
		}
		if (!this.loaded) {
			// Never save a pad that was not read first; loading merges and saves the typing.
			await this.load();
			if (!this.loaded) this.retryLater();
			return;
		}
		const snapshot = this.content;
		const base = {version: this.version, content: this.baseContent};
		const run = (async () => {
			try {
				const response = await http.put<ScratchpadPayload>(Endpoints.KONVO_SCRATCHPAD, {
					body: {content: snapshot, base_version: base.version, base_content: base.content, client_id: this.clientId},
					mode: 'silent',
				});
				if (!response.ok) throw new Error(`save failed with ${response.status}`);
				runInAction(() => {
					const body = response.body;
					const saved = typeof body?.content === 'string' ? body.content : snapshot;
					if (body) {
						this.version = body.version ?? this.version;
						this.updatedAt = body.updated_at ?? this.updatedAt;
					}
					this.baseContent = saved;
					if (this.content === snapshot) {
						// The server may have merged another device's lines in.
						this.content = saved;
						this.dirty = false;
						this.status = 'saved';
					} else if (saved !== snapshot) {
						// Typed on while saving, and the server merged: bring its lines in too.
						const merged = mergeLines(snapshot, this.content, saved);
						if (!merged.conflict) this.content = merged.text;
					}
				});
				if (this.dirty) this.scheduleSave();
			} catch (error) {
				logger.warn('Failed to save scratchpad', error);
				this.retryLater();
			}
		})();
		this.inFlight = run;
		try {
			await run;
		} finally {
			if (this.inFlight === run) this.inFlight = null;
		}
	}

	private retryLater(): void {
		runInAction(() => {
			this.status = 'offline';
		});
		if (this.retryTimer != null) clearTimeout(this.retryTimer);
		this.retryTimer = setTimeout(() => {
			this.retryTimer = null;
			if (this.dirty) void this.save();
		}, RETRY_MS);
	}

	/** Earlier versions the server kept, newest first. */
	async history(): Promise<Array<ScratchpadRevisionSummary>> {
		const response = await http.get<{revisions?: Array<ScratchpadRevisionSummary>}>(
			Endpoints.KONVO_SCRATCHPAD_HISTORY,
			{mode: 'silent'},
		);
		if (!response.ok) throw new Error(`history failed with ${response.status}`);
		return response.body?.revisions ?? [];
	}

	/** Puts an earlier version back. What it replaces goes into the history, so it can be undone. */
	async restore(version: number): Promise<boolean> {
		this.flush();
		if (this.inFlight) await this.inFlight;
		const response = await http.post<ScratchpadPayload>(Endpoints.KONVO_SCRATCHPAD_RESTORE, {
			body: {version, client_id: this.clientId},
			mode: 'silent',
		});
		if (!response.ok || typeof response.body?.content !== 'string') return false;
		const body = response.body;
		runInAction(() => {
			this.content = body.content;
			this.baseContent = body.content;
			this.version = body.version;
			this.updatedAt = body.updated_at ?? null;
			this.loaded = true;
			this.dirty = false;
			this.status = 'saved';
			if (!this.pads.some((pad) => pad.name === this.activePad)) this.selectPad(this.pads[0]?.name ?? DEFAULT_PAD);
		});
		return true;
	}

	private bindListeners(): void {
		if (this.listenersBound || typeof window === 'undefined') return;
		this.listenersBound = true;
		window.addEventListener('online', () => {
			if (this.dirty) void this.save();
			else void this.load();
		});
		window.addEventListener('focus', () => {
			if (!this.dirty) void this.load();
		});
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') this.flush();
		});
		window.addEventListener('pagehide', () => this.flush());
	}
}

export default new KonvoScratchpadStore();
